import { numericTone, PanelTitle } from './ui/MetricPanel';
import { Link } from 'react-router-dom';
import { usePollingResource } from '../hooks/usePollingResource';
import type { OperationDecisions } from '../api/system';
import type { OperationOverview } from '../api/types';
import { fmtDuration, fmtKst, fmtNumber, fmtPct } from '../lib/format';
import { StatusChip } from './ui/Primitives';
import ResourceNotice from './ResourceNotice';
const num = (value: number | null | undefined, digits = 2) => fmtNumber(value, { digits });
export default function StrategyMonitor({ overview, revision = 0, executionFailed = false }: {
    overview?: OperationOverview | null;
    revision?: number;
    executionFailed?: boolean;
}) {
    const resource = usePollingResource<OperationDecisions>(`/operation/decisions?_refresh=${revision}`, 15000);
    const data = resource.data;
    const blocked = overview?.kill_switch === true;
    const executionKnown = overview?.core_confirmed === true && !overview.stale && !executionFailed;
    return <section className="card" id="strategy-monitor" aria-label="종목별 전략 판단">
    <div className="card-title-row">
      <PanelTitle>C1 · 종목별 전략 판단</PanelTitle>
      <StatusChip tone={blocked ? 'danger' : executionKnown ? 'ok' : 'warn'}>
        {blocked ? '신규 진입 차단' : executionKnown ? '코어 준비됨' : '실행 상태 확인 필요'}
      </StatusChip>
      <Link className="research-link" to="/operation/positions">포지션·계정 →</Link>
    </div>
    <ResourceNotice {...resource}/>
    <p className="monitor-rule">{data?.entry_rule ?? '저장된 C1 판단 근거를 조회합니다.'}</p>
    <p className="muted-note">신호 {data?.params.tf_minutes ?? '—'}분 · EMA {data?.params.ema_fast ?? '—'}/{data?.params.ema_slow ?? '—'} · 손절 {data?.params.stop_atr ?? '—'} ATR · 종목별 노출 {data?.params.exposure ?? '—'}배 · 뉴스·매크로는 자문 자료</p>
    <div className="symbol-monitor-grid">
      {(data?.symbols ?? []).map(s => {
        const duration = data?.params.tf_minutes;
        const progressKnown = typeof s.partial_minutes === 'number' && Number.isFinite(s.partial_minutes) &&
          typeof duration === 'number' && Number.isFinite(duration) && duration > 0 && s.partial_minutes >= 0 && s.partial_minutes <= duration;
        return <article className="symbol-monitor" key={s.symbol}>
        <div className="card-title-row"><h4>{s.symbol}</h4>
          <StatusChip tone={s.stale || !s.ready || resource.error ? 'warn' : 'info'}>
            {s.stale || resource.error ? '관측 확인 필요' : s.position_side === 'FLAT' ? '미보유' : s.position_side ?? '미관측'}
          </StatusChip>
        </div>
        <p className="symbol-decision">{blocked ? '킬스위치로 신규 진입 차단 · ' : ''}{s.message}</p>
        <dl className="monitor-facts">
          <div><dt>마크 가격</dt><dd>{num(s.mark_price)}</dd></div>
          <div><dt>확정 목표</dt><dd>{s.target ?? '—'}</dd></div>
          <div><dt>EMA {data?.params.ema_fast}</dt><dd>{num(s.ema_fast)}</dd></div>
          <div><dt>EMA {data?.params.ema_slow}</dt><dd>{num(s.ema_slow)}</dd></div>
          <div><dt>EMA 간격</dt><dd className={`value-${numericTone(s.ema_gap_pct)}`}>{fmtPct(s.ema_gap_pct, { sign: true })}</dd></div>
          <div><dt>ATR</dt><dd>{num(s.atr)}</dd></div>
        </dl>
        <dl className="monitor-facts strategy-account-facts">
          <div><dt>분배 자산</dt><dd>{num(s.equity)} USDT</dd></div>
          <div><dt>실현 순손익</dt><dd className={`value-${numericTone(s.closed_net)}`}>{num(s.closed_net)} USDT</dd></div>
          <div><dt>미실현 손익</dt><dd className={`value-${numericTone(s.unrealized_pnl)}`}>{num(s.unrealized_pnl)} USDT</dd></div>
          <div><dt>완료 거래</dt><dd>{s.closed_trades ?? '—'}건</dd></div>
        </dl>
        {s.position_side && s.position_side !== 'FLAT' && <dl className="monitor-facts position-facts">
          <div><dt>진입가</dt><dd>{num(s.entry_price)}</dd></div>
          <div><dt>손절가</dt><dd>{num(s.stop_price)}</dd></div>
          <div><dt>수량</dt><dd>{num(s.qty, 6)}</dd></div>
          <div><dt>진입 시각</dt><dd>{fmtKst(s.entered_at)}</dd></div>
        </dl>}
        <p className="monitor-clock">다음 확정 봉 종료 <strong>{fmtKst(s.next_signal_close_at, { seconds: false })} KST</strong></p>
        {progressKnown && <progress className={`strategy-bar-progress ${s.partial_valid === false ? 'has-gap' : ''}`} max={duration} value={s.partial_minutes!} aria-label={`${s.symbol} 진행 봉 시간 ${s.partial_minutes}/${duration}분 · ${s.partial_valid === true ? '연속 수집' : s.partial_valid === false ? '봉 공백' : '연속성 미확인'}`}/>}
        <p className="muted-note">진행 봉 {s.partial_minutes ?? '—'}/{data?.params.tf_minutes ?? '—'}분 · {s.partial_valid === true ? '연속 수집' : s.partial_valid === false ? '봉 공백' : '새 봉 대기'}<br />
          확정 봉 {s.history_bars}/{s.required_bars ?? '—'} · 누적 공백 {s.historical_gaps ?? '—'}<br />
          최근 처리 봉 종료 {fmtKst(s.processed_close_at)} KST · 지연 {s.lag_seconds != null ? fmtDuration(s.lag_seconds) : '—'}</p>
        {Object.keys(s.rejections).length > 0 && <p className="research-alert">저장된 진입 거절 누계: {Object.entries(s.rejections).map(([reason, count]) => `${reason} ${count}회`).join(' · ')}</p>}
      </article>;
      })}
    </div>
    <p className="muted-note">{data?.signal_clock_note} {data?.exit_rule}<br />기록 {fmtKst(data?.as_of)} KST · 상태 버전 {data?.state_version ?? '—'} · {data?.source ?? '원본 확인 중'}</p>
  </section>;
}
